import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Sparkles, ArrowRight } from 'lucide-react';

export default function FounderNotePage() {
    return (
        <main className="main">
            {/* Hero Section */}
            <section className="hero" style={{
                minHeight: '300px',
                background: 'linear-gradient(193deg, rgb(242, 255, 168) 0%, rgb(255, 154, 255) 100%)'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h1 style={{ fontSize: '48px', marginBottom: '24px' }}>Founder's Note</h1>
                    <p style={{
                        fontSize: '20px',
                        maxWidth: '800px',
                        margin: '0 auto',
                        color: 'var(--text-secondary)',
                        lineHeight: '1.6'
                    }}>
                        The Value of Our Heritage
                    </p>
                </div>
            </section>


            {/* Main Content */}
            <section style={{ padding: '80px 0' }}>
                <div className="container">
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        {/* Opening Statement */}
                        <div style={{
                            fontSize: '18px',
                            lineHeight: '1.8',
                            color: 'var(--text-secondary)',
                            marginBottom: '48px'
                        }}>
                            <p style={{ marginBottom: '24px' }}>
                                At <strong>ATAM Connect</strong>, we believe that traditional arts and music are not just cultural
                                expressions — they are strategic assets for our social cohesion, identity, and community resilience.
                            </p>
                        </div>

                        {/* Key Values Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '24px',
                            marginBottom: '48px'
                        }}>
                            <div className="card" style={{
                                padding: '32px',
                                textAlign: 'center',
                                background: 'var(--bg-card)',
                                border: '2px solid #06b6d420'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: '#06b6d420',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <Heart size={32} style={{ color: '#06b6d4' }} />
                                </div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#06b6d4' }}>
                                    Heritage
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    Preserving cultural traditions that define who we are
                                </p>
                            </div>

                            <div className="card" style={{
                                padding: '32px',
                                textAlign: 'center',
                                background: 'var(--bg-card)',
                                border: '2px solid #f43f5e20'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: '#f43f5e20',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <Users size={32} style={{ color: '#f43f5e' }} />
                                </div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#f43f5e' }}>
                                    Community
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    Building connections across generations
                                </p>
                            </div>

                            <div className="card" style={{
                                padding: '32px',
                                textAlign: 'center',
                                background: 'var(--bg-card)',
                                border: '2px solid #a855f720'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: '#a855f720',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <Sparkles size={32} style={{ color: '#a855f7' }} />
                                </div>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#a855f7' }}>
                                    Innovation
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    Creating modern platforms for traditional arts
                                </p>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="card" style={{ padding: '48px', marginBottom: '48px' }}>
                            <div style={{
                                fontSize: '16px',
                                lineHeight: '1.8',
                                color: 'var(--text-secondary)'
                            }}>
                                <p style={{ marginBottom: '24px' }}>
                                    For generations, traditional art forms were embedded in everyday life. They were participatory,
                                    inclusive, and inherently communal — designed not just for elite performance but for shared experiences.
                                    These practices helped build stronger communities, pass on values, and mark key life moments with
                                    meaning and unity.
                                </p>

                                <p style={{ marginBottom: '24px' }}>
                                    Today, however, these arts face rapid decline — not due to a lack of value, but a lack of visibility
                                    and support. As our world moves forward at speed, we risk leaving behind the very cultural tools that
                                    bind us. In a globalised society, preserving identity is not just an act of nostalgia; it is a foundation
                                    for inclusive progress.
                                </p>

                                <p style={{ marginBottom: '24px' }}>
                                    Traditional arts are uniquely positioned to foster intergenerational connection, social belonging,
                                    and mental well-being — all of which have a real impact on how societies function.
                                </p>

                                <div style={{
                                    background: 'var(--bg-secondary)',
                                    padding: '32px',
                                    borderRadius: '16px',
                                    borderLeft: '4px solid var(--primary)',
                                    marginBottom: '24px'
                                }}>
                                    <h3 style={{
                                        fontSize: '20px',
                                        fontWeight: '600',
                                        marginBottom: '16px',
                                        color: 'var(--text-primary)'
                                    }}>
                                        Our Mission
                                    </h3>
                                    <p style={{ marginBottom: '16px' }}>
                                        <strong>ATAM Connect</strong> serves as a unified platform to protect, preserve, and promote
                                        Singapore's Indian Heritage and Arts through:
                                    </p>
                                    <ul style={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: 0
                                    }}>
                                        <li style={{
                                            marginBottom: '12px',
                                            paddingLeft: '24px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                left: 0,
                                                color: 'var(--primary)',
                                                fontWeight: '700'
                                            }}>•</span>
                                            <strong>Centralized Discovery:</strong> Making cultural events accessible to everyone through
                                            a single, easy-to-use platform
                                        </li>
                                        <li style={{
                                            marginBottom: '12px',
                                            paddingLeft: '24px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                left: 0,
                                                color: 'var(--primary)',
                                                fontWeight: '700'
                                            }}>•</span>
                                            <strong>Community Building:</strong> Connecting audiences with performers, organizations,
                                            and cultural practitioners
                                        </li>
                                        <li style={{
                                            marginBottom: '12px',
                                            paddingLeft: '24px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                left: 0,
                                                color: 'var(--primary)',
                                                fontWeight: '700'
                                            }}>•</span>
                                            <strong>Visibility & Support:</strong> Amplifying the reach of partner organizations and
                                            individual artists
                                        </li>
                                        <li style={{
                                            paddingLeft: '24px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                left: 0,
                                                color: 'var(--primary)',
                                                fontWeight: '700'
                                            }}>•</span>
                                            <strong>Modern Innovation:</strong> Using technology to create platforms that allow
                                            traditions to thrive in a contemporary context
                                        </li>
                                    </ul>
                                </div>

                                <p style={{ marginBottom: '24px' }}>
                                    This is not just about the past — it's about building a future that remembers where it came from.
                                    By bringing together scattered cultural events into one cohesive platform, <strong>ATAM Connect</strong>
                                    helps ensure that traditional arts remain visible, vibrant, and accessible to new generations.
                                </p>

                                <p>
                                    We invite you to be part of this journey — not just to witness performances, but to actively participate
                                    in preserving and celebrating the cultural capital of tomorrow.
                                </p>
                            </div>
                        </div>
                        <div className="container">
                            <div style={{
                                maxWidth: '800px',
                                margin: '0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '32px',
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}>
                                {/* Founder Image */}
                                <div style={{
                                    width: '90px',
                                    height: '90px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '2px solid var(--gray-200)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    flexShrink: 0
                                }}>
                                    <img
                                        src="https://www.atam.sg/images/JGK-Profile-Pic.png"
                                        alt="Japhire Gopi Kannan"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>

                                {/* Founder Info */}
                                <div style={{ textAlign: 'left', flex: '1', minWidth: '250px' }}>
                                    <h2 style={{
                                        fontSize: '28px',
                                        fontWeight: '700',
                                        marginBottom: '8px',
                                        color: 'var(--text-primary)'
                                    }}>
                                        Japhire Gopi Kannan
                                    </h2>
                                    <p style={{
                                        fontSize: '16px',
                                        color: 'var(--text-muted)',
                                        marginBottom: '0'
                                    }}>
                                        The Founder of ATAM
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </section>

            {/* CTA Section */}
            <section style={{
                padding: '80px 0',
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                borderRadius: '24px',
                margin: '0 24px 64px'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h2 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        marginBottom: '16px',
                        color: 'white'
                    }}>
                        Join the Movement
                    </h2>
                    <p style={{
                        color: 'rgba(255,255,255,0.9)',
                        marginBottom: '32px',
                        fontSize: '18px',
                        maxWidth: '600px',
                        margin: '0 auto 32px'
                    }}>
                        Explore events, connect with your community, and be part of preserving our cultural heritage.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/events" className="btn" style={{
                            background: 'white',
                            color: 'var(--primary)',
                            fontWeight: '600'
                        }}>
                            Discover Events <ArrowRight size={18} />
                        </Link>
                        <Link to="/contact" className="btn" style={{
                            background: 'transparent',
                            border: '2px solid white',
                            color: 'white'
                        }}>
                            Get Involved
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
